import React, { useEffect, useState, useMemo, useCallback } from "react";
import Box from '@mui/material/Box';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableRow, { tableRowClasses } from '@mui/material/TableRow';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import L from 'leaflet';
import {
    Button,
    IconButton,
    Card,
    CardContent,
    Tabs,
    Tab,
    Typography,
    Switch,
    Stack,
    Grid,
    Paper,
    Select,
    MenuItem,
    FormControl,
    CircularProgress,
    circularProgressClasses,
    Tooltip,
    Checkbox
} from '@mui/material';
import {
    BarChart as BarChartIcon,
    DarkMode as DarkModeIcon,
    LightMode as LightModeIcon,
    List as ListIcon,
    ShowChart as ChartIcon,
    Circle as CircleIcon,
    CheckBox
} from '@mui/icons-material';
import {
    LineChart,
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import { useDispatch, useSelector } from 'react-redux';
import { createPortal } from 'react-dom';
import { useMap } from 'react-leaflet';
import { setShowMounted, setModal } from "../../store/reducers/helpersReducer";
import { toggleDarkMode } from "../../store/reducers/stateReducer";

// Memoized color functions
const getColor = (percent) => {
    if (percent < 30) return "#cc0000";
    if (percent < 50) return "#cc6600";
    if (percent < 70) return "#cc9900";
    if (percent < 90) return "#4da6ff";
    return "#4dff4d";
};

const lightenColor = (hex, percent = 0.5) => {
    const num = parseInt(hex.replace("#", ""), 16);
    const r = Math.min(255, Math.round((num >> 16) * (1 - percent) + 50 * percent));
    const g = Math.min(255, Math.round(((num >> 8) & 0x00ff) * (1 - percent) + 50 * percent));
    const b = Math.min(255, Math.round((num & 0x0000ff) * (1 - percent) + 50 * percent));
    return `rgb(${r},${g},${b})`;
};

// Memoized date parsing
const parseDate = (str) => {
    const months = {
        января: 0, февраля: 1, марта: 2, апреля: 3, мая: 4, июня: 5,
        июля: 6, августа: 7, сентября: 8, октября: 9, ноября: 10, декабря: 11
    };
    const match = str.match(/(\d{1,2}) (\S+) (\d{4})/);
    if (!match) return new Date(NaN);
    const [, day, month, year] = match;
    return new Date(Number(year), months[month], Number(day));
};

// Memoized period key generation
const getPeriodKey = (dateStr, mode) => {
    const d = new Date(parseInt(dateStr));
    if (isNaN(d.getTime())) return 'unknown';

    const days = ['вс.', 'пн.', 'вт.', 'ср.', 'чт.', 'пт.', 'сб.'];
    const months = [
        'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
        'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'
    ];
    const day = d.getDate();
    const weekday = days[d.getDay()];
    const month = months[d.getMonth()];
    const year = d.getFullYear();

    if (mode === 'daily') return `${weekday} ${day} ${month} ${year}`;
    if (mode === 'weekly') {
        const firstDayOfYear = new Date(year, 0, 1);
        const weekNumber = Math.ceil(((d - firstDayOfYear) / 86400000 + firstDayOfYear.getDay() + 1) / 7);
        return `${weekNumber}нд. ${month} ${year}`;
    }
    if (mode === 'monthly') return `${month} ${year}`;
    if (mode === 'yearly') return `${year}`;
    return 'unknown';
};

// Memoized data aggregation
const aggregateData = (data, mode) => {
    if (!data) return {};

    // Для режима "Всего" суммируем все данные в одну запись
    if (mode === 'total') {
        const totalStats = {
            poles: {
                pillar04: {
                    wooden: { one_support: 0, two_support: 0, three_support: 0 },
                    concrete: { one_support: 0, two_support: 0, three_support: 0 },
                    steel: { one_support: 0, two_support: 0, three_support: 0 }
                },
                pillar10: {
                    wooden: { one_support: 0, two_support: 0, three_support: 0 },
                    concrete: { one_support: 0, two_support: 0, three_support: 0 },
                    steel: { one_support: 0, two_support: 0, three_support: 0 }
                },
                'pillar04-10': {
                    wooden: { one_support: 0, two_support: 0, three_support: 0 },
                    concrete: { one_support: 0, two_support: 0, three_support: 0 },
                    steel: { one_support: 0, two_support: 0, three_support: 0 }
                }
            },
            supports: {
                pillar04: { c1: 0, c2: 0, sv95: 0, sv110: 0 },
                pillar10: { c1: 0, c2: 0, sv95: 0, sv110: 0 },
                'pillar04-10': { c1: 0, c2: 0, sv95: 0, sv110: 0 }
            },
            groundings: { wooden04_95: 0, wooden04_110: 0, concrete04_95: 0, concrete04_110: 0, wooden10_95: 0, wooden10_110: 0, concrete10_95: 0, concrete10_110: 0, wooden04_10_95: 0, wooden04_10_110: 0, concrete04_10_95: 0, concrete04_10_110: 0 },
            sip: { 'СИП-2': 0, 'СИП-3': 0, 'СИП-4': 0 },
            runn: 0,
            ktp: 0
        };

        for (const [label, stats] of Object.entries(data)) {
            // Агрегация poles по типам опор
            for (const pillarType of ['pillar04', 'pillar10', 'pillar04-10']) {
                for (const material of ['wooden', 'concrete', 'steel']) {
                    totalStats.poles[pillarType][material].one_support += stats.poles[pillarType][material].one_support;
                    totalStats.poles[pillarType][material].two_support += stats.poles[pillarType][material].two_support;
                    totalStats.poles[pillarType][material].three_support += stats.poles[pillarType][material].three_support;
                }
            }

            // Агрегация supports по типам опор
            for (const pillarType of ['pillar04', 'pillar10', 'pillar04-10']) {
                totalStats.supports[pillarType].c1 += stats.supports[pillarType].c1;
                totalStats.supports[pillarType].c2 += stats.supports[pillarType].c2;
                totalStats.supports[pillarType].sv95 += stats.supports[pillarType].sv95;
                totalStats.supports[pillarType].sv110 += stats.supports[pillarType].sv110;
            }
            totalStats.groundings.wooden04_95 += stats.groundings.wooden04_95;
            totalStats.groundings.wooden04_110 += stats.groundings.wooden04_110;
            totalStats.groundings.concrete04_95 += stats.groundings.concrete04_95;
            totalStats.groundings.concrete04_110 += stats.groundings.concrete04_110;
            totalStats.groundings.wooden10_95 += stats.groundings.wooden10_95;
            totalStats.groundings.wooden10_110 += stats.groundings.wooden10_110;
            totalStats.groundings.concrete10_95 += stats.groundings.concrete10_95;
            totalStats.groundings.concrete10_110 += stats.groundings.concrete10_110;
            totalStats.groundings.wooden04_10_95 += stats.groundings.wooden04_10_95;
            totalStats.groundings.wooden04_10_110 += stats.groundings.wooden04_10_110;
            totalStats.groundings.concrete04_10_95 += stats.groundings.concrete04_10_95;
            totalStats.groundings.concrete04_10_110 += stats.groundings.concrete04_10_110;

            for (const k in stats.sip) {
                totalStats.sip[k] += stats.sip[k];
            }

            totalStats.runn += stats.runn;
            totalStats.ktp += stats.ktp;
        }

        return { 'Всего': totalStats };
    }

    const aggregated = {};

    for (const [label, stats] of Object.entries(data)) {
        const key = getPeriodKey(label, mode);

        if (!aggregated[key]) {
            aggregated[key] = {
                poles: {
                    pillar04: {
                        wooden: { one_support: 0, two_support: 0, three_support: 0 },
                        concrete: { one_support: 0, two_support: 0, three_support: 0 },
                        steel: { one_support: 0, two_support: 0, three_support: 0 }
                    },
                    pillar10: {
                        wooden: { one_support: 0, two_support: 0, three_support: 0 },
                        concrete: { one_support: 0, two_support: 0, three_support: 0 },
                        steel: { one_support: 0, two_support: 0, three_support: 0 }
                    },
                    'pillar04-10': {
                        wooden: { one_support: 0, two_support: 0, three_support: 0 },
                        concrete: { one_support: 0, two_support: 0, three_support: 0 },
                        steel: { one_support: 0, two_support: 0, three_support: 0 }
                    }
                },
                supports: {
                    pillar04: { c1: 0, c2: 0, sv95: 0, sv110: 0 },
                    pillar10: { c1: 0, c2: 0, sv95: 0, sv110: 0 },
                    'pillar04-10': { c1: 0, c2: 0, sv95: 0, sv110: 0 }
                },
                groundings: { wooden04_95: 0, wooden04_110: 0, concrete04_95: 0, concrete04_110: 0, wooden10_95: 0, wooden10_110: 0, concrete10_95: 0, concrete10_110: 0, wooden04_10_95: 0, wooden04_10_110: 0, concrete04_10_95: 0, concrete04_10_110: 0 },
                sip: { 'СИП-2': 0, 'СИП-3': 0, 'СИП-4': 0 },
                runn: 0,
                ktp: 0
            };
        }

        // Агрегация poles по типам опор
        for (const pillarType of ['pillar04', 'pillar10', 'pillar04-10']) {
            for (const material of ['wooden', 'concrete', 'steel']) {
                aggregated[key].poles[pillarType][material].one_support += stats.poles[pillarType][material].one_support;
                aggregated[key].poles[pillarType][material].two_support += stats.poles[pillarType][material].two_support;
                aggregated[key].poles[pillarType][material].three_support += stats.poles[pillarType][material].three_support;
            }
        }

        // Агрегация supports по типам опор
        for (const pillarType of ['pillar04', 'pillar10', 'pillar04-10']) {
            aggregated[key].supports[pillarType].c1 += stats.supports[pillarType].c1;
            aggregated[key].supports[pillarType].c2 += stats.supports[pillarType].c2;
            aggregated[key].supports[pillarType].sv95 += stats.supports[pillarType].sv95;
            aggregated[key].supports[pillarType].sv110 += stats.supports[pillarType].sv110;
        }
        aggregated[key].groundings.wooden04_95 += stats.groundings.wooden04_95;
        aggregated[key].groundings.wooden04_110 += stats.groundings.wooden04_110;
        aggregated[key].groundings.concrete04_95 += stats.groundings.concrete04_95;
        aggregated[key].groundings.concrete04_110 += stats.groundings.concrete04_110;
        aggregated[key].groundings.wooden10_95 += stats.groundings.wooden10_95;
        aggregated[key].groundings.wooden10_110 += stats.groundings.wooden10_110;
        aggregated[key].groundings.concrete10_95 += stats.groundings.concrete10_95;
        aggregated[key].groundings.concrete10_110 += stats.groundings.concrete10_110;
        aggregated[key].groundings.wooden04_10_95 += stats.groundings.wooden04_10_95;
        aggregated[key].groundings.wooden04_10_110 += stats.groundings.wooden04_10_110;
        aggregated[key].groundings.concrete04_10_95 += stats.groundings.concrete04_10_95;
        aggregated[key].groundings.concrete04_10_110 += stats.groundings.concrete04_10_110;

        for (const k in stats.sip) {
            aggregated[key].sip[k] += stats.sip[k];
        }

        aggregated[key].runn += stats.runn;
        aggregated[key].ktp += stats.ktp;
    }

    return aggregated;
};

const ValueTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main,
    fontWeight: 600,
    marginLeft: theme.spacing(1)
}));

// Memoized content renderer
const ContentRenderer = React.memo(({ stats, darkMode, formatNumber }) => {
    if (!stats) return null;
    const isMobile = window.innerWidth < 768;

    // Подсчет общего количества опор по материалам для всех типов опор
    let totalWoodenPoles = 0;
    let totalConcretePoles = 0;
    let totalSteelPoles = 0;

    for (const pillarType of ['pillar04', 'pillar10', 'pillar04-10']) {
        if (stats.poles && stats.poles[pillarType]) {
            const pillarData = stats.poles[pillarType];
            if (pillarData.wooden) {
                totalWoodenPoles += (pillarData.wooden.one_support || 0) +
                    (pillarData.wooden.two_support || 0) +
                    (pillarData.wooden.three_support || 0);
            }
            if (pillarData.concrete) {
                totalConcretePoles += (pillarData.concrete.one_support || 0) +
                    (pillarData.concrete.two_support || 0) +
                    (pillarData.concrete.three_support || 0);
            }
            if (pillarData.steel) {
                totalSteelPoles += (pillarData.steel.one_support || 0) +
                    (pillarData.steel.two_support || 0) +
                    (pillarData.steel.three_support || 0);
            }
        }
    }

    const totalPoles = totalWoodenPoles + totalConcretePoles + totalSteelPoles;

    // Подсчет отдельных значений для отображения детализации
    let woodenOne = 0, woodenTwo = 0, woodenThree = 0;
    let concreteOne = 0, concreteTwo = 0, concreteThree = 0;
    let steelOne = 0, steelTwo = 0, steelThree = 0;

    for (const pillarType of ['pillar04', 'pillar10', 'pillar04-10']) {
        if (stats.poles && stats.poles[pillarType]) {
            const pillarData = stats.poles[pillarType];
            if (pillarData.wooden) {
                woodenOne += pillarData.wooden.one_support || 0;
                woodenTwo += pillarData.wooden.two_support || 0;
                woodenThree += pillarData.wooden.three_support || 0;
            }
            if (pillarData.concrete) {
                concreteOne += pillarData.concrete.one_support || 0;
                concreteTwo += pillarData.concrete.two_support || 0;
                concreteThree += pillarData.concrete.three_support || 0;
            }
            if (pillarData.steel) {
                steelOne += pillarData.steel.one_support || 0;
                steelTwo += pillarData.steel.two_support || 0;
                steelThree += pillarData.steel.three_support || 0;
            }
        }
    }

    // Подсчет общего количества комплектующих для всех типов опор
    let totalSupports = 0;
    if (stats.supports) {
        for (const pillarType of ['pillar04', 'pillar10', 'pillar04-10']) {
            if (stats.supports[pillarType]) {
                const supportData = stats.supports[pillarType];
                totalSupports += (supportData.c1 || 0) +
                    (supportData.c2 || 0) +
                    (supportData.sv95 || 0) +
                    (supportData.sv110 || 0);
            }
        }
    }
    const totalSIP = Object.values(stats.sip).reduce((sum, val) => sum + val, 0);

    return (
        <Card sx={{
            bgcolor: darkMode ? 'grey.900' : 'white',
            color: darkMode ? 'white' : 'inherit',
            boxShadow: 1,
            borderRadius: { xs: 1, sm: 1 }
        }}>
            <CardContent sx={{
                p: { xs: 1, sm: 1.5 },
                '&:last-child': { pb: { xs: 1, sm: 1.5 } }
            }}>
                <Stack spacing={{ xs: 0.5, sm: 1 }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: { xs: 0.3, sm: 0.5 } }}>
                        <Typography component="div" variant="subtitle1" sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontWeight: 600,
                            fontSize: { xs: '0.9rem', sm: '0.9rem' }
                        }}>
                            <Box component="span" sx={{ mr: 1, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>1.</Box>
                            Опоры всего:
                            <ValueTypography sx={{ fontSize: { xs: '1rem', sm: '0.95rem' } }}>
                                {formatNumber(totalPoles)} шт
                            </ValueTypography>
                        </Typography>
                        {(totalPoles > 0) && (
                            <Stack spacing={{ xs: 0.5, sm: 0.8 }} sx={{ ml: { xs: 1.25, sm: 2 }, mt: { xs: 0.3, sm: 0.5 } }}>
                                {/* Отображение по типам опор */}
                                {['pillar04', 'pillar10', 'pillar04-10'].map(pillarType => {
                                    const pillarData = stats.poles?.[pillarType];
                                    if (!pillarData) return null;

                                    const pillarTypeName = pillarType === 'pillar04' ? '0,4кВ' :
                                        pillarType === 'pillar10' ? '10кВ' : '0,4-10кВ';

                                    const pillarTotal = (pillarData.wooden?.one_support || 0) + (pillarData.wooden?.two_support || 0) + (pillarData.wooden?.three_support || 0) +
                                        (pillarData.concrete?.one_support || 0) + (pillarData.concrete?.two_support || 0) + (pillarData.concrete?.three_support || 0) +
                                        (pillarData.steel?.one_support || 0) + (pillarData.steel?.two_support || 0) + (pillarData.steel?.three_support || 0);

                                    if (pillarTotal === 0) return null;

                                    return (
                                        <Box key={pillarType} sx={{
                                            border: 1,
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            p: { xs: 1, sm: 1.5 },
                                            bgcolor: darkMode ? 'grey.800' : 'grey.50'
                                        }}>
                                            <Typography component="div" variant="body2" sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                fontWeight: 600,
                                                mb: 1,
                                                color: darkMode ? 'primary.light' : 'primary.main'
                                            }}>
                                                <BarChartIcon sx={{ fontSize: { xs: 18, sm: 16 } }} />
                                                {pillarTypeName}:
                                                <ValueTypography sx={{ fontSize: { xs: '0.9rem', sm: '0.875rem' } }}>
                                                    {formatNumber(pillarTotal)} шт
                                                </ValueTypography>
                                            </Typography>

                                            <Stack spacing={{ xs: 0.3, sm: 0.4 }}>
                                                {/* Деревянные опоры */}
                                                {((pillarData.wooden?.one_support || 0) + (pillarData.wooden?.two_support || 0) + (pillarData.wooden?.three_support || 0)) > 0 && (
                                                    <Box>
                                                        <Typography component="div" variant="caption" sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            fontWeight: 500,
                                                            mb: 0.5
                                                        }}>
                                                            <CircleIcon sx={{ width: { xs: 8, sm: 6 }, height: { xs: 8, sm: 6 } }} />
                                                            Деревянные:
                                                            <ValueTypography sx={{ fontSize: { xs: '0.8rem', sm: '0.75rem' } }}>
                                                                {formatNumber((pillarData.wooden?.one_support || 0) + (pillarData.wooden?.two_support || 0) + (pillarData.wooden?.three_support || 0))} шт
                                                            </ValueTypography>
                                                        </Typography>
                                                        <Box sx={{ ml: 1.5 }}>
                                                            {(pillarData.wooden?.one_support || 0) > 0 && (
                                                                <Typography component="div" variant="caption" sx={{ display: 'flex', alignItems: 'center', py: 0.2 }}>
                                                                    • Одностоечные: <ValueTypography sx={{ fontSize: '0.75rem', ml: 0.5 }}>{formatNumber(pillarData.wooden.one_support)} шт</ValueTypography>
                                                                </Typography>
                                                            )}
                                                            {(pillarData.wooden?.two_support || 0) > 0 && (
                                                                <Typography component="div" variant="caption" sx={{ display: 'flex', alignItems: 'center', py: 0.2 }}>
                                                                    • Двухстоечные: <ValueTypography sx={{ fontSize: '0.75rem', ml: 0.5 }}>{formatNumber(pillarData.wooden.two_support)} шт</ValueTypography>
                                                                </Typography>
                                                            )}
                                                            {(pillarData.wooden?.three_support || 0) > 0 && (
                                                                <Typography component="div" variant="caption" sx={{ display: 'flex', alignItems: 'center', py: 0.2 }}>
                                                                    • Трехстоечные: <ValueTypography sx={{ fontSize: '0.75rem', ml: 0.5 }}>{formatNumber(pillarData.wooden.three_support)} шт</ValueTypography>
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                )}

                                                {/* Бетонные опоры */}
                                                {((pillarData.concrete?.one_support || 0) + (pillarData.concrete?.two_support || 0) + (pillarData.concrete?.three_support || 0)) > 0 && (
                                                    <Box>
                                                        <Typography component="div" variant="caption" sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            fontWeight: 500,
                                                            mb: 0.5
                                                        }}>
                                                            <CircleIcon sx={{ width: { xs: 8, sm: 6 }, height: { xs: 8, sm: 6 } }} />
                                                            Железобетонные:
                                                            <ValueTypography sx={{ fontSize: { xs: '0.8rem', sm: '0.75rem' } }}>
                                                                {formatNumber((pillarData.concrete?.one_support || 0) + (pillarData.concrete?.two_support || 0) + (pillarData.concrete?.three_support || 0))} шт
                                                            </ValueTypography>
                                                        </Typography>
                                                        <Box sx={{ ml: 1.5 }}>
                                                            {(pillarData.concrete?.one_support || 0) > 0 && (
                                                                <Typography component="div" variant="caption" sx={{ display: 'flex', alignItems: 'center', py: 0.2 }}>
                                                                    • Одностоечные: <ValueTypography sx={{ fontSize: '0.75rem', ml: 0.5 }}>{formatNumber(pillarData.concrete.one_support)} шт</ValueTypography>
                                                                </Typography>
                                                            )}
                                                            {(pillarData.concrete?.two_support || 0) > 0 && (
                                                                <Typography component="div" variant="caption" sx={{ display: 'flex', alignItems: 'center', py: 0.2 }}>
                                                                    • Двухстоечные: <ValueTypography sx={{ fontSize: '0.75rem', ml: 0.5 }}>{formatNumber(pillarData.concrete.two_support)} шт</ValueTypography>
                                                                </Typography>
                                                            )}
                                                            {(pillarData.concrete?.three_support || 0) > 0 && (
                                                                <Typography component="div" variant="caption" sx={{ display: 'flex', alignItems: 'center', py: 0.2 }}>
                                                                    • Трехстоечные: <ValueTypography sx={{ fontSize: '0.75rem', ml: 0.5 }}>{formatNumber(pillarData.concrete.three_support)} шт</ValueTypography>
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                )}

                                                {/* Стальные опоры */}
                                                {((pillarData.steel?.one_support || 0) + (pillarData.steel?.two_support || 0) + (pillarData.steel?.three_support || 0)) > 0 && (
                                                    <Box>
                                                        <Typography component="div" variant="caption" sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            fontWeight: 500,
                                                            mb: 0.5
                                                        }}>
                                                            <CircleIcon sx={{ width: { xs: 8, sm: 6 }, height: { xs: 8, sm: 6 } }} />
                                                            Стальные:
                                                            <ValueTypography sx={{ fontSize: { xs: '0.8rem', sm: '0.75rem' } }}>
                                                                {formatNumber((pillarData.steel?.one_support || 0) + (pillarData.steel?.two_support || 0) + (pillarData.steel?.three_support || 0))} шт
                                                            </ValueTypography>
                                                        </Typography>
                                                        <Box sx={{ ml: 1.5 }}>
                                                            {(pillarData.steel?.one_support || 0) > 0 && (
                                                                <Typography component="div" variant="caption" sx={{ display: 'flex', alignItems: 'center', py: 0.2 }}>
                                                                    • Одностоечные: <ValueTypography sx={{ fontSize: '0.75rem', ml: 0.5 }}>{formatNumber(pillarData.steel.one_support)} шт</ValueTypography>
                                                                </Typography>
                                                            )}
                                                            {(pillarData.steel?.two_support || 0) > 0 && (
                                                                <Typography component="div" variant="caption" sx={{ display: 'flex', alignItems: 'center', py: 0.2 }}>
                                                                    • Двухстоечные: <ValueTypography sx={{ fontSize: '0.75rem', ml: 0.5 }}>{formatNumber(pillarData.steel.two_support)} шт</ValueTypography>
                                                                </Typography>
                                                            )}
                                                            {(pillarData.steel?.three_support || 0) > 0 && (
                                                                <Typography component="div" variant="caption" sx={{ display: 'flex', alignItems: 'center', py: 0.2 }}>
                                                                    • Трехстоечные: <ValueTypography sx={{ fontSize: '0.75rem', ml: 0.5 }}>{formatNumber(pillarData.steel.three_support)} шт</ValueTypography>
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Stack>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        )}
                    </Box>

                    <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: { xs: 0.3, sm: 0.5 } }}>
                        <Typography component="div" variant="subtitle1" sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontWeight: 600,
                            fontSize: { xs: '0.9rem', sm: '0.9rem' }
                        }}>
                            <Box component="span" sx={{ mr: 1, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>2.</Box>
                            Стойки всего:
                            <ValueTypography sx={{ fontSize: { xs: '1rem', sm: '0.95rem' } }}>
                                {formatNumber(totalSupports)} шт
                            </ValueTypography>
                        </Typography>
                        {totalSupports > 0 && (
                            <Stack spacing={{ xs: 0.5, sm: 0.8 }} sx={{ ml: { xs: 1.25, sm: 2 }, mt: { xs: 0.3, sm: 0.5 } }}>
                                {/* Отображение комплектующих по типам опор */}
                                {['pillar04', 'pillar10', 'pillar04-10'].map(pillarType => {
                                    const supportData = stats.supports?.[pillarType];
                                    if (!supportData) return null;

                                    const pillarTypeName = pillarType === 'pillar04' ? '0,4кВ' :
                                        pillarType === 'pillar10' ? '10кВ' : '0,4-10кВ';

                                    const supportTotal = (supportData.c1 || 0) + (supportData.c2 || 0) + (supportData.sv95 || 0) + (supportData.sv110 || 0);

                                    if (supportTotal === 0) return null;

                                    return (
                                        <Box key={pillarType} sx={{
                                            border: 1,
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            p: { xs: 1, sm: 1.5 },
                                            bgcolor: darkMode ? 'grey.800' : 'grey.50'
                                        }}>
                                            <Typography component="div" variant="body2" sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                fontWeight: 600,
                                                mb: 1,
                                                color: darkMode ? 'primary.light' : 'primary.main'
                                            }}>
                                                <BarChartIcon sx={{ fontSize: { xs: 18, sm: 16 } }} />
                                                {pillarTypeName}:
                                                <ValueTypography sx={{ fontSize: { xs: '0.9rem', sm: '0.875rem' } }}>
                                                    {formatNumber(supportTotal)} шт
                                                </ValueTypography>
                                            </Typography>

                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0, flexDirection: 'column' }}>
                                                {[
                                                    { key: 'c1', label: 'C1 9,5м' },
                                                    { key: 'c2', label: 'C2 11м' },
                                                    { key: 'sv95', label: 'CВ 9,5-3с' },
                                                    { key: 'sv110', label: 'CВ 110-5' }
                                                ].map((item) => {
                                                    if ((supportData[item.key] || 0) > 0) {
                                                        return (
                                                            <Typography key={item.key} component="div" variant="caption" sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                py: { xs: 0.2, sm: 0.2 }
                                                            }}>
                                                                <CircleIcon sx={{ width: { xs: 8, sm: 6 }, height: { xs: 8, sm: 6 }, mr: 0.5 }} />
                                                                {item.label}:
                                                                <ValueTypography sx={{ fontSize: { xs: '0.8rem', sm: '0.75rem' } }}>
                                                                    {formatNumber(supportData[item.key])} шт
                                                                </ValueTypography>
                                                            </Typography>
                                                        )
                                                    } else {
                                                        return null
                                                    }
                                                }).filter(item => item !== null)}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        )}
                    </Box>

                    <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: { xs: 0.3, sm: 0.5 } }}>
                        <Typography component="div" variant="subtitle1" sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontWeight: 600,
                            fontSize: { xs: '0.9rem', sm: '0.9rem' }
                        }}>
                            <Box component="span" sx={{ mr: 1, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>3.</Box>
                            Заземление всего:
                            <ValueTypography sx={{ fontSize: { xs: '1rem', sm: '0.95rem' } }}>
                                {formatNumber(stats.groundings.wooden04_95 + stats.groundings.wooden04_110 + stats.groundings.concrete04_95 + stats.groundings.concrete04_110 + stats.groundings.wooden10_95 + stats.groundings.wooden10_110 + stats.groundings.concrete10_95 + stats.groundings.concrete10_110 + stats.groundings.wooden04_10_95 + stats.groundings.wooden04_10_110 + stats.groundings.concrete04_10_95 + stats.groundings.concrete04_10_110)} шт
                            </ValueTypography>
                        </Typography>
                        {stats.groundings.wooden04_95 + stats.groundings.wooden04_110 + stats.groundings.concrete04_95 + stats.groundings.concrete04_110 + stats.groundings.wooden10_95 + stats.groundings.wooden10_110 + stats.groundings.concrete10_95 + stats.groundings.concrete10_110 > 0 && (
                            <Stack spacing={{ xs: 0.3, sm: 0.5 }} sx={{ ml: { xs: 1.25, sm: 2 }, mt: { xs: 0.3, sm: 0.5 } }}>
                                {stats.groundings.wooden04_95 + stats.groundings.wooden04_110 + stats.groundings.concrete04_95 + stats.groundings.concrete04_110 > 0 && (
                                    <Typography component="div" variant="body2" sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        fontWeight: 500
                                    }}>
                                        <BarChartIcon sx={{ fontSize: { xs: 18, sm: 16 } }} />
                                        0,4кВ:
                                        <ValueTypography sx={{ fontSize: { xs: '1rem', sm: '0.875rem' } }}>
                                            {formatNumber(stats.groundings.wooden04_95 + stats.groundings.wooden04_110 + stats.groundings.concrete04_95 + stats.groundings.concrete04_110)} шт
                                        </ValueTypography>
                                    </Typography>
                                )}
                                {stats.groundings.wooden10_95 + stats.groundings.wooden10_110 + stats.groundings.concrete10_95 + stats.groundings.concrete10_110 > 0 && (
                                    <Typography component="div" variant="body2" sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        fontWeight: 500
                                    }}>
                                        <BarChartIcon sx={{ fontSize: { xs: 18, sm: 16 } }} />
                                        10кВ:
                                        <ValueTypography sx={{ fontSize: { xs: '1rem', sm: '0.875rem' } }}>
                                            {formatNumber(stats.groundings.wooden10_95 + stats.groundings.wooden10_110 + stats.groundings.concrete10_95 + stats.groundings.concrete10_110)} шт
                                        </ValueTypography>
                                    </Typography>
                                )}
                                {stats.groundings.wooden04_10_95 + stats.groundings.wooden04_10_110 + stats.groundings.concrete04_10_95 + stats.groundings.concrete04_10_110 > 0 && (
                                    <Typography component="div" variant="body2" sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        fontWeight: 500
                                    }}>
                                        <BarChartIcon sx={{ fontSize: { xs: 18, sm: 16 } }} />
                                        0,4-10кВ:
                                        <ValueTypography sx={{ fontSize: { xs: '1rem', sm: '0.875rem' } }}>
                                            {formatNumber(stats.groundings.wooden04_10_95 + stats.groundings.wooden04_10_110 + stats.groundings.concrete04_10_95 + stats.groundings.concrete04_10_110)} шт
                                        </ValueTypography>
                                    </Typography>
                                )}
                            </Stack>
                        )}
                    </Box>

                    <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: { xs: 0.3, sm: 0.5 } }}>
                        <Typography component="div" variant="subtitle1" sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontWeight: 600,
                            fontSize: { xs: '0.9rem', sm: '0.9rem' }
                        }}>
                            <Box component="span" sx={{ mr: 1, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>4.</Box>
                            СИП всего:
                            <ValueTypography sx={{ fontSize: { xs: '1rem', sm: '0.95rem' } }}>
                                {formatNumber(Math.round((totalSIP / 1000) * 1000) / 1000, 3)} км
                            </ValueTypography>
                        </Typography>
                        {totalSIP > 0 && (
                            <Box sx={{
                                ml: { xs: 1.25, sm: 2 },
                                mt: { xs: 0.3, sm: 0.5 },
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0,
                                flexDirection: 'column'
                            }}>
                                {Object.entries(stats.sip).sort().map(([type, count]) => (
                                    count > 0 && (
                                        <Typography key={type} component="div" variant="caption" sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            py: { xs: 0, sm: 0 }
                                        }}>
                                            <CircleIcon sx={{ width: { xs: 8, sm: 6 }, height: { xs: 8, sm: 6 }, mr: 0.5 }} />
                                            {type}:
                                            <ValueTypography sx={{ fontSize: { xs: '0.8rem', sm: '0.75rem' } }}>
                                                {formatNumber(Math.round((count / 1000) * 1000) / 1000, 3)} км
                                            </ValueTypography>
                                        </Typography>
                                    )
                                ))}
                            </Box>
                        )}
                    </Box>

                    <Box sx={{
                        display: 'flex',
                        gap: { xs: 2, sm: 3 },
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        <Typography component="div" variant="subtitle1" sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontWeight: 600,
                            fontSize: { xs: '0.9rem', sm: '0.9rem' }
                        }}>
                            <Box component="span" sx={{ mr: 1, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>5.</Box>
                            РУНН:
                            <ValueTypography sx={{ fontSize: { xs: '1rem', sm: '0.95rem' } }}>
                                {formatNumber(stats.runn)} шт
                            </ValueTypography>
                        </Typography>
                        <Typography component="div" variant="subtitle1" sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontWeight: 600,
                            fontSize: { xs: '0.9rem', sm: '0.9rem' }
                        }}>
                            <Box component="span" sx={{ mr: 1, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>6.</Box>
                            КТП:
                            <ValueTypography sx={{ fontSize: { xs: '1rem', sm: '0.95rem' } }}>
                                {formatNumber(stats.ktp)} шт
                            </ValueTypography>
                        </Typography>
                    </Box>
                </Stack>
            </CardContent>
        </Card >
    );
});

// Main component
const StatisticControl = React.memo((props) => {
    const dispatch = useDispatch();
    const map = useMap();
    const darkMode = useSelector(state => state.state.darkMode);

    // State
    const [dates, setDates] = useState([]);
    const [data, setData] = useState(null);
    const [hidden, setHidden] = useState({});
    const [isOpen, setIsOpen] = useState(true);
    const [viewMode, setViewMode] = useState('monthly');
    const [stage, setStage] = useState('5');
    const [showChart, setShowChart] = useState(false);
    const [selectedKey, setSelectedKey] = useState('');
    const [tabValue, setTabValue] = useState(0);
    const [percent, setPercent] = useState(0);
    const [isDisplayMounted, setIsDisplayMounted] = useState(() =>
        localStorage.getItem('showMounted') ? JSON.parse(localStorage.getItem('showMounted')) : false
    );
    // Accept buttonPosition prop
    const { buttonPosition } = props || {};

    // Redux selectors
    // const { electricity, specs, filter } = useSelector(state => state.state);
    const mounted = useSelector(state => state.state.mounted)
    const electricity = useSelector(state => state.state.electricity)
    const electricityF = useSelector(state => state.state.electricityFiltered)
    const specs = useSelector(state => state.state.specs)
    const specs_new = useSelector(state => state.state.specs_new)
    const filter = useSelector(state => state.state.filter)

    const electricityFiltered = useMemo(() => {
        // const tpFilter = new Set(filter.tp || [])
        // const projectsFilter = new Set(filter.projects || [])
        // let transformers = electricity.transformers.filter(tr => !tpFilter.has(tr.fid) && (tr.projects || [0]).some(proj => projectsFilter.has(proj)))
        let transformers = electricityF.transformers
        const transformersIds = new Set(transformers.map(t => t.fid))
        let pillars = electricity.pillars.filter(p => (stage === '5' ? ['3', '4'] : [stage]).includes(p.stage) && transformersIds.has(Number(p.tp_id)))
        let fiders = electricity.fiders.filter(p => (stage === '5' ? ['3', '4'] : [stage]).includes(p.stage) && transformersIds.has(Number(p.tp_id)))
        let pillarsRunn = electricity.pillarsRunn.filter(p => (stage === '5' ? ['3', '4'] : [stage]).includes(p.stage) && transformersIds.has(Number(p.tp_id)))
        // if (tpFilter) {
        //     pillars = pillars.filter(p => !tpFilter.includes(parseInt(p.tp_id)))
        //     fiders = fiders.filter(p => !tpFilter.includes(parseInt(p.tp_id)))
        //     transformers = transformers.filter(p => !tpFilter.includes(p.fid))
        // }
        return {
            ...electricity,
            transformers: transformers,
            pillarsRunn: pillarsRunn,
            pillars: pillars,
            fiders: fiders
        }
    }, [electricity, stage, filter])

    const mountedFiltered = useMemo(() => {
        if (!mounted) return null
        console.log(electricityFiltered)
        console.log(mounted)
        const transformersIds = new Set(electricityFiltered?.transformers?.map(t => t.fid) || [])
        const pillarsIds = new Set(electricityFiltered?.pillars?.map(t => t.fid) || [])
        const runnIds = new Set(electricityFiltered?.runn?.map(t => t.fid) || [])
        const fidersIds = new Set(electricityFiltered?.fiders?.map(t => t.id) || [])
        console.log(transformersIds, pillarsIds, runnIds, fidersIds)
        return {
            transformers: mounted.transformers?.filter(m => transformersIds.has(m.fid) && m.status > 0) || [],
            pillars: mounted.pillars?.filter(m => pillarsIds.has(m.fid) && m.status > 0) || [],
            // opn: mounted.opn?.filter(m => m.status > 0) || [],
            // zvz: mounted.zvz?.filter(m => m.status > 0) || [],
            // rlnd: mounted.rlnd?.filter(m => m.status > 0) || [],
            fiders: mounted.fiders?.filter(m => fidersIds.has(m.fid)) || [],
            runn: mounted.runn?.filter(m => runnIds.has(m.fid) && m.status > 0) || []
        }
    }, [mounted, electricityFiltered])

    console.log(mountedFiltered)

    useEffect(() => {
        if (filter.stage && filter.stage.length > 0) {
            setStage(filter.stage[0].toString())
        }
    }, [filter.stage])

    // Memoized calculations
    const aggregated = useMemo(() => aggregateData(data, viewMode), [data, viewMode]);
    const keys = useMemo(() => {
        if (viewMode === 'total') {
            return Object.keys(aggregated);
        }
        return Object.keys(aggregated).sort((a, b) => parseDate(a) - parseDate(b));
    }, [aggregated, viewMode]);

    // Memoized format function
    const formatNumber = useCallback((value, fractionDigits = 0) => {
        return value.toLocaleString('ru-RU', {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits
        });
    }, []);

    // Memoized handlers
    const handleTabChange = useCallback((event, newValue) => {
        setTabValue(newValue);
        setSelectedKey(keys[newValue]);
    }, [keys]);

    const handleViewModeChange = useCallback((e) => {
        setViewMode(e.target.value);
    }, []);

    const handleStageChange = useCallback((e) => {
        setStage(e.target.value);
    }, []);

    const handleToggleChart = useCallback(() => {
        setShowChart(prev => !prev);
    }, []);

    const handleToggleDark = useCallback(() => {
        dispatch(toggleDarkMode());
    }, [dispatch]);

    const handleToggleHidden = useCallback(() => {
        dispatch(setModal(null))
    }, [dispatch]);

    const handleToggleDisplayMounted = useCallback(() => {
        setIsDisplayMounted(prev => !prev);
    }, []);

    const handleLegendClick = useCallback((e) => {
        const { dataKey } = e;
        setHidden(prev => ({ ...prev, [dataKey]: !prev[dataKey] }));
    }, []);

    // Effects with optimized dependencies

    useEffect(() => {
        const tpFilter = filter.tp
        let pillars = electricity.pillars.filter(p => p.stage === '4')
        if (tpFilter) {
            pillars = pillars.filter(p => !tpFilter.find(f => f === parseInt(p.tp_id)))
        }
        const pillarsCount = pillars.length;
        // const mountedCount = mounted.pillars.length;
        const mountedCount = pillars.filter(p => mountedFiltered.pillars.find(m => m.fid === p.fid))?.length || 0

        setPercent(Math.round((mountedCount / pillarsCount) * 100) || 0);
    }, [electricity.pillars, mountedFiltered.pillars, filter]);

    useEffect(() => {
        setDates([]);
        setData(null);
        if (stage === '5') {
            // const dates = new Set()
            const dates = []
            Object.keys(mountedFiltered).forEach(key => {
                mountedFiltered[key].forEach(m => {
                    if (key === 'fiders') {
                        Object.entries(m.state.segments).map(([key, value]) => {
                            const dateStart = new Date(value.date).setHours(0, 0, 0, 0)
                            const dateEnd = new Date(value.date).setHours(23, 59, 59, 999)
                            if (!dates.find(d => d[0] === dateStart && d[1] === dateEnd)) {
                                dates.push([dateStart, dateEnd])
                            }
                        })
                    } else {
                        const dateStart = new Date(m.created_at).setHours(0, 0, 0, 0)
                        const dateEnd = new Date(m.created_at).setHours(23, 59, 59, 999)
                        if (!dates.find(d => d[0] === dateStart && d[1] === dateEnd)) {
                            dates.push([dateStart, dateEnd])
                        }
                    }

                })
            });
            setDates(dates)
        } else {
            const dates = []
            const objects = [...electricityFiltered.pillars, ...electricityFiltered.fiders, ...electricityFiltered.pillarsRunn]
            objects
                .filter(p => p.stage === stage)
                .map(p => {
                    const date = new Date(p.date_history)
                    const dateStart = new Date(date.setHours(0, 0, 0, 0)).getTime()
                    const dateEnd = new Date(date.setHours(23, 59, 59, 999)).getTime()
                    if (!dates.find(d => d[0] === dateStart && d[1] === dateEnd)) {
                        dates.push([dateStart, dateEnd])
                    }
                });
            setDates(dates);
        }
    }, [stage, electricityFiltered, mountedFiltered]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            dispatch(setShowMounted(isDisplayMounted));
        }, 100);
        return () => clearTimeout(timeoutId);
    }, [isDisplayMounted, dispatch]);

    // Complex data processing effect - memoized with careful dependencies
    useEffect(() => {

        if (dates.length === 0) return;
        let tmp = {};
        dates.sort((a, b) => a[0] - b[0]).forEach((d, i) => {
            const start = d[0];
            const end = d[1];
            let pillarsF = stage === '5'
                ? mountedFiltered.pillars?.filter(p => new Date(p.created_at).getTime() >= start && new Date(p.created_at).getTime() <= end)
                : electricityFiltered.pillars.filter(p => {
                    const pDate = new Date(p.date_history).getTime();
                    return pDate >= start && pDate <= end;
                });
            let fidersF = stage === '5'
                ? mountedFiltered.fiders?.
                    flatMap(f => Object.entries(f.state.segments).filter(([key, value]) => value.status > 0 && new Date(value.date).getTime() >= start && new Date(value.date).getTime() <= end).map(([key, value]) => {
                       
                        if (value.sip_class && Array.isArray(value.sip_class)) {
                            return value.sip_class.map(sip_class => {
                                const sip = specs.sip_class.find(s => s.id === sip_class) 
                                return {
                                    fid: f.fid,
                                    distance: value.length,
                                    mounted: { name: sip.name_str, quantity: parseInt(sip.qty_chains) || 0 },
                                }
                            })
                        }
                        return []
                    })).flat() || []
                : electricityFiltered.fiders.filter(f => {
                    const fDate = new Date(f.date_history).getTime();
                    return fDate >= start && fDate <= end;
                });
            console.log(fidersF)
            let transformersF = stage !== '5'
                ? electricityFiltered.transformers 
                : mountedFiltered.transformers.filter(p => new Date(p.created_at).getTime() >= start && new Date(p.created_at).getTime() <= end)
            let pillarsRunnF = stage !== '5'
                ? electricityFiltered.pillarsRunn.filter(p => {
                    const pDate = new Date(p.date_history).getTime();
                    return pDate >= start && pDate <= end;
                })
                : mountedFiltered.runn.filter(p => new Date(p.created_at).getTime() >= start && new Date(p.created_at).getTime() <= end)

            const fidersSegments = stage === 5 ? fidersF : [];

            if (stage !== '5') {
                console.log(fidersF.filter(t => t.sip_class?.find(sc => sc.name_str?.includes('СИП-3'))))
                fidersF.forEach(f => {
                    
                    const distances = []
                    let coordinates = f.coordinates;
                    // const connectedPillars = JSON.parse(f.connected_pillars);
                    for (let i = 0; i < coordinates.length - 1; i++) {
                        const from = new L.LatLng(coordinates[i][0], coordinates[i][1]);
                        const to = new L.LatLng(coordinates[i + 1][0], coordinates[i + 1][1]);
                        distances.push(from.distanceTo(to));
                    }
                    if (Array.isArray(f.sip_class) && f.sip_class.length > 0) {
                        f.sip_class.map((sip, i) => {
                            // const s = specs_new[spec] || null
                            if (sip) {
                                fidersSegments.push({
                                    mounted: { name: sip.name_str, quantity: parseInt(sip.qty_chains) || 0 },
                                    distance: distances.reduce((a, v) => a + v, 0)
                                })
                            }
                        })

                    }
                    // if (!f.connected) return;

                    // let connectedObjects = [];
                    // for (let index = 0; index < f.connected.length - 1; index++) {
                    //     if (!f.connected[index] || !f.connected[index + 1]) continue;
                    //     if (!f.connected[index].id || !f.connected[index + 1].id) continue;
                    //     connectedObjects.push([f.connected[index], f.connected[index + 1], index]);
                    // }
                    // connectedObjects.forEach(cp => {
                    //     const object1 = cp[0].type === 'pillar' ? electricityFiltered.pillars.find(p => p.fid === cp[0].id) : electricityFiltered.transformers.find(t => t.fid === cp[0].id)
                    //     const object2 = cp[1].type === 'pillar' ? electricityFiltered.pillars.find(p => p.fid === cp[1].id) : electricityFiltered.transformers.find(t => t.fid === cp[1].id)

                    //     if (object1 && object2) {
                    // const object1Spec = cp[0].type === 'pillar' ? specs_new[object1.spec]?.[Number(object1.spec_complect)] : null
                    // const object2Spec = cp[1].type === 'pillar' ? specs_new[object2.spec]?.[Number(object2.spec_complect)] : null
                    // if (!object1Spec || !object2Spec) return;
                    // const object1Chains = cp[0].type === 'pillar' ? (object1Spec?.chains || Object.values(specs_new[object1.spec])[0]?.chains || 1) : Infinity
                    // const object2Chains = cp[1].type === 'pillar' ? (object2Spec?.chains || Object.values(specs_new[object2.spec])[0]?.chains || 1) : Infinity
                    // let quantity = Math.min(object1Chains, object2Chains);
                    // [object1Spec, object2Spec].forEach(spec => {
                    //     if (!spec) return;
                    //     if (spec.name.includes('одноцепная')) quantity = Math.min(quantity, 1);
                    //     else if (spec.name.includes('двухцепная')) quantity = Math.min(quantity, 2);
                    //     else if (spec.name.includes('трехцепная')) quantity = Math.min(quantity, 3);
                    //     else if (spec.name.includes('четырехцепная')) quantity = Math.min(quantity, 4);
                    // });
                    // console.log(object1, object2)
                    // const distance = object1.connected?.find(c => c.id === object2.fid)?.distance ||
                    //     object2.connected?.find(c => c.id === object1.fid)?.distance || 0;

                    // const spec = specs.fiders.find(s => s.id === f.spec);
                    // if (Array.isArray(f.sip_class) && f.sip_class.length > 0) {
                    //     f.sip_class.map((sip, i) => {
                    //         // const s = specs_new[spec] || null
                    //         if (sip) {
                    //             fidersSegments.push({
                    //                 mounted: { name: sip.name_str, quantity: parseInt(sip.qty_chains) || 0 },
                    //                 distance
                    //             })
                    //         }
                    //     })

                    // }

                    // const spec = specs_new[f.spec] || null;

                    // fidersSegments.push({
                    //     mounted: { name: spec?.name, quantity },
                    //     distance
                    // });
                    // }
                    // });
                });
                fidersF = fidersSegments;
                
                pillarsF = pillarsF.map(p => {
                    if (!p.spec) return p;
                    const spec = specs_new[p.spec]?.[Number(p.spec_complect)] || Object.values(specs_new[p.spec])[0] || null;
                    return spec ? { ...p, spec } : p;
                });
            } else {
                pillarsF = pillarsF.map(p => {
                    if (!p.spec) return null;
                    const pillar = electricity.pillars.find(m => m.fid === p.fid);
                    const spec = specs_new[p.spec]?.[Number(p.spec_complect)] || Object.values(specs_new[p.spec])[0] || null;
                
                    if (!pillar || !spec) return null
                    return { ...pillar, spec };
                }).filter(f => f);
                
                // console.log(fidersF)
                // fidersF = fidersF.map(f => {
                //     if (!f.spec) return null;
                //     const fider = electricityFiltered.fiders.find(m => m.id === f.fid)
                //     if (!fider) return null
                //     return f
                // }).filter(f => f)
            }
            // console.log(pillarsF?.filter(p => p.type === 'pillar10' && p.spec.complect.find(c => c.name.toLowerCase().includes('с2'))))
            const data = {

                poles: {
                    pillar04: {
                        wooden: {
                            one_support: pillarsF?.filter(p => p.type === 'pillar04' && p.spec && p.spec.material_type === "1" && p.supports_count === 1).length || 0,
                            two_support: pillarsF?.filter(p => p.type === 'pillar04' && p.spec && p.spec.material_type === "1" && p.supports_count === 2).length || 0,
                            three_support: pillarsF?.filter(p => p.type === 'pillar04' && p.spec && p.spec.material_type === "1" && p.supports_count === 3).length || 0,
                        },
                        concrete: {
                            one_support: pillarsF?.filter(p => p.type === 'pillar04' && p.spec && p.spec.material_type === "2" && p.supports_count === 1).length || 0,
                            two_support: pillarsF?.filter(p => p.type === 'pillar04' && p.spec && p.spec.material_type === "2" && p.supports_count === 2).length || 0,
                            three_support: pillarsF?.filter(p => p.type === 'pillar04' && p.spec && p.spec.material_type === "2" && p.supports_count === 3).length || 0,
                        },
                        steel: {
                            one_support: pillarsF?.filter(p => p.type === 'pillar04' && p.spec && p.spec.material_type === "3" && p.supports_count === 1).length || 0,
                            two_support: pillarsF?.filter(p => p.type === 'pillar04' && p.spec && p.spec.material_type === "3" && p.supports_count === 2).length || 0,
                            three_support: pillarsF?.filter(p => p.type === 'pillar04' && p.spec && p.spec.material_type === "3" && p.supports_count === 3).length || 0,
                        }
                    },
                    pillar10: {
                        wooden: {
                            one_support: pillarsF?.filter(p => p.type === 'pillar10' && p.spec && p.spec.material_type === "1" && p.supports_count === 1).length || 0,
                            two_support: pillarsF?.filter(p => p.type === 'pillar10' && p.spec && p.spec.material_type === "1" && p.supports_count === 2).length || 0,
                            three_support: pillarsF?.filter(p => p.type === 'pillar10' && p.spec && p.spec.material_type === "1" && p.supports_count === 3).length || 0,
                        },
                        concrete: {
                            one_support: pillarsF?.filter(p => p.type === 'pillar10' && p.spec && p.spec.material_type === "2" && p.supports_count === 1).length || 0,
                            two_support: pillarsF?.filter(p => p.type === 'pillar10' && p.spec && p.spec.material_type === "2" && p.supports_count === 2).length || 0,
                            three_support: pillarsF?.filter(p => p.type === 'pillar10' && p.spec && p.spec.material_type === "2" && p.supports_count === 3).length || 0,
                        },
                        steel: {
                            one_support: pillarsF?.filter(p => p.type === 'pillar10' && p.spec && p.spec.material_type === "3" && p.supports_count === 1).length || 0,
                            two_support: pillarsF?.filter(p => p.type === 'pillar10' && p.spec && p.spec.material_type === "3" && p.supports_count === 2).length || 0,
                            three_support: pillarsF?.filter(p => p.type === 'pillar10' && p.spec && p.spec.material_type === "3" && p.supports_count === 3).length || 0,
                        }
                    },
                    'pillar04-10': {
                        wooden: {
                            one_support: pillarsF?.filter(p => p.type === 'pillar04-10' && p.spec && p.spec.material_type === "1" && p.supports_count === 1).length || 0,
                            two_support: pillarsF?.filter(p => p.type === 'pillar04-10' && p.spec && p.spec.material_type === "1" && p.supports_count === 2).length || 0,
                            three_support: pillarsF?.filter(p => p.type === 'pillar04-10' && p.spec && p.spec.material_type === "1" && p.supports_count === 3).length || 0,
                        },
                        concrete: {
                            one_support: pillarsF?.filter(p => p.type === 'pillar04-10' && p.spec && p.spec.material_type === "2" && p.supports_count === 1).length || 0,
                            two_support: pillarsF?.filter(p => p.type === 'pillar04-10' && p.spec && p.spec.material_type === "2" && p.supports_count === 2).length || 0,
                            three_support: pillarsF?.filter(p => p.type === 'pillar04-10' && p.spec && p.spec.material_type === "2" && p.supports_count === 3).length || 0,
                        },
                        steel: {
                            one_support: pillarsF?.filter(p => p.type === 'pillar04-10' && p.spec && p.spec.material_type === "3" && p.supports_count === 1).length || 0,
                            two_support: pillarsF?.filter(p => p.type === 'pillar04-10' && p.spec && p.spec.material_type === "3" && p.supports_count === 2).length || 0,
                            three_support: pillarsF?.filter(p => p.type === 'pillar04-10' && p.spec && p.spec.material_type === "3" && p.supports_count === 3).length || 0,
                        }
                    }
                },
                supports: {
                    pillar04: {
                        c1: pillarsF?.filter(p => p.type === 'pillar04' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('с1')))
                            ?.reduce((acc, curr) => acc + curr.spec.supports_count, 0) || 0,
                        c2: pillarsF?.filter(p => p.type === 'pillar04' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('с2')))
                            ?.reduce((acc, curr) => acc + curr.spec.supports_count, 0) || 0,
                        sv95: pillarsF?.filter(p => p.type === 'pillar04' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('св-95')))
                            ?.reduce((acc, curr) => acc + curr.spec.supports_count, 0) || 0,
                        sv110: pillarsF?.filter(p => p.type === 'pillar04' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('св-110')))
                            ?.reduce((acc, curr) => acc + curr.spec.supports_count, 0) || 0
                    },
                    pillar10: {
                        c1: pillarsF?.filter(p => p.type === 'pillar10' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('с1')))
                            ?.reduce((acc, curr) => acc + curr.spec.supports_count, 0) || 0,
                        c2: pillarsF?.filter(p => p.type === 'pillar10' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('с2')))
                            ?.reduce((acc, curr) => acc + curr.spec.supports_count, 0) || 0,
                        sv95: pillarsF?.filter(p => p.type === 'pillar10' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('св-95')))
                            ?.reduce((acc, curr) => acc + curr.spec.supports_count, 0) || 0,
                        sv110: pillarsF?.filter(p => p.type === 'pillar10' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('св-110')))
                            ?.reduce((acc, curr) => acc + curr.spec.supports_count, 0) || 0
                    },
                    'pillar04-10': {
                        c1: pillarsF?.filter(p => p.type === 'pillar04-10' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('с1')))
                            ?.reduce((acc, curr) => acc + curr.spec.supports_count, 0) || 0,
                        c2: pillarsF?.filter(p => p.type === 'pillar04-10' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('с2')))
                            ?.reduce((acc, curr) => acc + curr.spec.supports_count, 0) || 0,
                        sv95: pillarsF?.filter(p => p.type === 'pillar04-10' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('св-95')))
                            ?.reduce((acc, curr) => acc + curr.spec.supports_count, 0) || 0,
                        sv110: pillarsF?.filter(p => p.type === 'pillar04-10' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('св-110')))
                            ?.reduce((acc, curr) => acc + curr.spec.supports_count, 0) || 0
                    }
                },
                groundings: {
                    wooden04_95: pillarsF?.filter(p => p.grounding && p.type === 'pillar04' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('с1')))
                        ?.reduce((acc, curr) => acc + curr.grounding, 0) || 0,
                    wooden04_110: pillarsF?.filter(p => p.grounding && p.type === 'pillar04' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('с2')))
                        ?.reduce((acc, curr) => acc + curr.grounding, 0) || 0,
                    concrete04_95: pillarsF?.filter(p => p.grounding && p.type === 'pillar04' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('св-95')))
                        ?.reduce((acc, curr) => acc + curr.grounding, 0) || 0,
                    concrete04_110: pillarsF?.filter(p => p.grounding && p.type === 'pillar04' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('св-110')))
                        ?.reduce((acc, curr) => acc + curr.grounding, 0) || 0,
                    wooden10_95: pillarsF?.filter(p => p.grounding && p.type === 'pillar10' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('с1')))
                        ?.reduce((acc, curr) => acc + curr.grounding, 0) || 0,
                    wooden10_110: pillarsF?.filter(p => p.grounding && p.type === 'pillar10' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('с2')))
                        ?.reduce((acc, curr) => acc + curr.grounding, 0) || 0,
                    concrete10_95: pillarsF?.filter(p => p.grounding && p.type === 'pillar10' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('св-95')))
                        ?.reduce((acc, curr) => acc + curr.grounding, 0) || 0,
                    concrete10_110: pillarsF?.filter(p => p.grounding && p.type === 'pillar10' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('св-110')))
                        ?.reduce((acc, curr) => acc + curr.grounding, 0) || 0,
                    wooden04_10_95: pillarsF?.filter(p => p.grounding && p.type === 'pillar04-10' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('с1')))
                        ?.reduce((acc, curr) => acc + curr.grounding, 0) || 0,
                    wooden04_10_110: pillarsF?.filter(p => p.grounding && p.type === 'pillar04-10' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('с2')))
                        ?.reduce((acc, curr) => acc + curr.grounding, 0) || 0,
                    concrete04_10_95: pillarsF?.filter(p => p.grounding && p.type === 'pillar04-10' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('св-95')))
                        ?.reduce((acc, curr) => acc + curr.grounding, 0) || 0,
                    concrete04_10_110: pillarsF?.filter(p => p.grounding && p.type === 'pillar04-10' && p.spec && p.spec.complect.find(c => c.name.toLowerCase().includes('св-110')))
                        ?.reduce((acc, curr) => acc + curr.grounding, 0) || 0,



                },
                sip: {
                    'СИП-2': fidersF?.filter(m => m.mounted.name.toLowerCase().includes('сип-2'))
                        .reduce((acc, curr) => acc + curr.mounted.quantity * curr.distance, 0) || 0,
                    'СИП-3': fidersF?.filter(m => m.mounted.name.toLowerCase().includes('сип-3'))
                        .reduce((acc, curr) => acc + curr.mounted.quantity * curr.distance * 3, 0) || 0,
                    'СИП-4': fidersF?.filter(m => m.mounted.name.toLowerCase().includes('сип-4'))
                        .reduce((acc, curr) => acc + curr.mounted.quantity * curr.distance, 0) || 0
                },
                sip_route: {
                    'СИП-2': fidersF?.filter(m => m.mounted.name.toLowerCase().includes('сип-2'))
                        .reduce((acc, curr) => acc + curr.distance, 0) || 0,
                    'СИП-3': fidersF?.filter(m => m.mounted.name.toLowerCase().includes('сип-3'))
                        .reduce((acc, curr) => acc + curr.distance, 0) || 0,
                    'СИП-4': fidersF?.filter(m => m.mounted.name.toLowerCase().includes('сип-4'))
                        .reduce((acc, curr) => acc + curr.distance, 0) || 0
                },
                runn: pillarsRunnF?.length || 0,
                ktp: transformersF?.length || 0
            };
            tmp[d] = data;
        });

        setData(tmp);
    }, [dates, electricityFiltered, mounted, specs, stage]);

    useEffect(() => {
        if (keys.length > 0) {
            setSelectedKey(keys[keys.length - 1]);
            setTabValue(keys.length - 1);
        } else {
            setSelectedKey('');
            setTabValue(0);
        }
    }, [keys]);

    // Memoized chart data
    const chartData = useMemo(() =>
        keys.map(k => {
            const data = aggregated[k];
            if (!data) return { name: k };

            // Подсчет общего количества опор по материалам для всех типов опор
            let totalWooden = 0;
            let totalConcrete = 0;
            let totalSteel = 0;

            for (const pillarType of ['pillar04', 'pillar10', 'pillar04-10']) {
                if (data.poles[pillarType]) {
                    totalWooden += (data.poles[pillarType].wooden?.one_support || 0) +
                        (data.poles[pillarType].wooden?.two_support || 0) +
                        (data.poles[pillarType].wooden?.three_support || 0);
                    totalConcrete += (data.poles[pillarType].concrete?.one_support || 0) +
                        (data.poles[pillarType].concrete?.two_support || 0) +
                        (data.poles[pillarType].concrete?.three_support || 0);
                    totalSteel += (data.poles[pillarType].steel?.one_support || 0) +
                        (data.poles[pillarType].steel?.two_support || 0) +
                        (data.poles[pillarType].steel?.three_support || 0);
                }
            }

            // Подсчет общего количества комплектующих для всех типов опор
            let totalC1 = 0;
            let totalC2 = 0;
            let totalSv95 = 0;
            let totalSv110 = 0;

            for (const pillarType of ['pillar04', 'pillar10', 'pillar04-10']) {
                if (data.supports[pillarType]) {
                    totalC1 += data.supports[pillarType].c1 || 0;
                    totalC2 += data.supports[pillarType].c2 || 0;
                    totalSv95 += data.supports[pillarType].sv95 || 0;
                    totalSv110 += data.supports[pillarType].sv110 || 0;
                }
            }

            return {
                name: k,
                wooden: totalWooden,
                concrete: totalConcrete,
                steel: totalSteel,
                c1: totalC1,
                c2: totalC2,
                sv95: totalSv95,
                sv110: totalSv110,
                ...data.groundings,
                ...data.sip,
                runn: data.runn || 0,
                ktp: data.ktp || 0
            };
        }), [keys, aggregated]
    );
    // Memoized styles with dynamic positioning
    const dialogBoxStyles = useMemo(() => {
        const isMobile = window.innerWidth < 768;
        const baseStyles = {
            position: 'absolute',
            zIndex: 1300,
            bgcolor: 'background.paper',
            boxShadow: 3,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            opacity: 0.95
        };

        // Mobile fullscreen layout
        if (isMobile) {
            return {
                ...baseStyles,
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100vw',
                height: '100vh',
                maxWidth: '100vw',
                maxHeight: '100vh',
                borderRadius: 0,
                margin: 0
            };
        }

        // Desktop positioning near button
        const desktopStyles = {
            ...baseStyles,
            maxWidth: '800px',
            width: 'auto',
            minWidth: '500px',
            height: 'auto',
            maxHeight: '80vh',
            borderRadius: 1
        };

        // Calculate position based on button position
        if (buttonPosition) {
            const modalWidth = 800;
            const modalHeight = 600;

            // Position to the right of the button with some spacing
            let left = buttonPosition.right + 10;
            let top = buttonPosition.top;

            // Check if modal would go off screen on the right
            if (left + modalWidth > window.innerWidth) {
                // Position to the left of the button instead
                left = buttonPosition.left - modalWidth - 10;
            }

            // Ensure modal doesn't go off screen on the left
            if (left < 10) {
                left = 10;
            }

            // Check if modal would go off screen at the bottom
            if (top + modalHeight > window.innerHeight) {
                top = window.innerHeight - modalHeight - 10;
            }

            // Ensure modal doesn't go off screen at the top
            if (top < 10) {
                top = 10;
            }

            return {
                ...desktopStyles,
                left: `${left}px`,
                top: `${top}px`,
                margin: 0
            };
        }

        // Default desktop positioning (fallback)
        return {
            ...desktopStyles,
            margin: '10px 10px 5px 5px'
        };
    }, [buttonPosition]);

    const headerStyles = useMemo(() => {
        const isMobile = window.innerWidth < 768;

        return {
            p: isMobile ? '6px 12px' : '8px 16px',
            fontSize: isMobile ? 14 : 18,
            textAlign: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            position: 'relative',
            minHeight: isMobile ? '40px' : 'auto',
            bgcolor: darkMode ? 'black' : 'grey.50',
            color: darkMode ? 'white' : 'inherit',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 0.5,
            flexShrink: 0
        };
    }, [darkMode]);

    const contentStyles = useMemo(() => {
        const isMobile = window.innerWidth < 768;

        return {
            flex: 1,
            overflow: 'auto',
            position: 'relative',
            px: isMobile ? 1 : 2,
            py: isMobile ? 0.75 : 1,
            bgcolor: darkMode ? 'black' : 'grey.50',
            color: darkMode ? 'white' : 'inherit',
            transition: 'background-color 0.3s',
            minHeight: 0 // Important for flex overflow
        };
    }, [darkMode]);

    return (
        createPortal(
            <Box sx={{ zIndex: 1202, display: 'flex', order: 7 }}>
                <Box sx={dialogBoxStyles}>
                    {/* Dialog Title */}
                    <Box sx={headerStyles}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress
                                variant="determinate"
                                sx={(theme) => ({
                                    color: lightenColor(getColor(percent), 0.5),
                                    ...theme.applyStyles('dark', {
                                        color: lightenColor(getColor(percent), 0.5),
                                    }),
                                })}
                                size={34}
                                thickness={7}
                                value={100}
                            />
                            <CircularProgress
                                variant="determinate"
                                sx={(theme) => ({
                                    color: getColor(percent),
                                    position: 'absolute',
                                    [`& .${circularProgressClasses.circle}`]: {
                                        strokeLinecap: 'round',
                                    },
                                    ...theme.applyStyles('dark', {
                                        color: getColor(percent),
                                    }),
                                })}
                                size={34}
                                thickness={7}
                                value={percent}
                            />
                            <Typography
                                variant="caption"
                                component="div"
                                sx={{
                                    position: 'absolute',
                                    color: 'inherit',
                                    fontSize: 11
                                }}
                            >
                                {`${Math.round(percent || 0)}%`}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, justifyContent: 'center' }}>
                            <BarChartIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
                            <Typography variant="h6" component="span" sx={{ fontSize: { xs: 14, sm: 18 } }}>
                                Статистика строительства
                            </Typography>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={handleToggleHidden}
                            size="small"
                            sx={{
                                color: (theme) => theme.palette.grey[500],
                                minWidth: { xs: 36, sm: 'auto' },
                                minHeight: { xs: 36, sm: 'auto' },
                                p: { xs: 1, sm: 0.5 }
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Dialog Content */}
                    <Box sx={contentStyles}>
                        {isOpen && (
                            <Box sx={{ transition: 'all 0.3s' }}>
                                <Stack spacing={{ xs: 0.2, sm: 0.5 }} sx={{ mb: { xs: 0.2, sm: 0.5 } }}>

                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: { xs: 0.5, sm: 1 },
                                        flexDirection: { xs: 'row', sm: 'row' },
                                        py: { xs: 0.25, sm: 0 }
                                    }}>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            // justifyContent: 'space-between',
                                            flexWrap: 'wrap',
                                            gap: { xs: 0.5, sm: 1 },
                                            width: { xs: 'auto', sm: 'auto' },
                                            justifyContent: { xs: 'flex-start', sm: 'flex-start' }
                                        }}>
                                            <FormControl size="small" sx={{ minWidth: { xs: 80, sm: 'auto' } }}>

                                                <Select
                                                    value={stage}
                                                    onChange={handleStageChange}
                                                    sx={{
                                                        bgcolor: darkMode ? 'grey.800' : 'white',
                                                        color: darkMode ? 'white' : 'grey.800',
                                                        borderColor: darkMode ? 'grey.700' : 'grey.300',
                                                        minHeight: { xs: 28, sm: 'auto' },
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                        '& .MuiSelect-select': {
                                                            py: { xs: 0.5, sm: 1 }
                                                        }
                                                    }}
                                                    MenuProps={{
                                                        PaperProps: {
                                                            sx: {
                                                                '& .MuiMenuItem-root': {
                                                                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                                                    padding: { xs: '4px 8px', sm: '4px 8px' },
                                                                    minHeight: { xs: '28px', sm: '32px' }
                                                                },
                                                                '& .MuiListSubheader-root': {
                                                                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                                                    padding: { xs: '4px 8px', sm: '4px 8px' },
                                                                    lineHeight: 1.2,
                                                                    fontWeight: 'bold',
                                                                    color: 'black'
                                                                }
                                                            }
                                                        },
                                                    }}
                                                >
                                                    <MenuItem value="1">Предпроектное обследование</MenuItem>
                                                    <MenuItem value="2">Рабочая документация</MenuItem>
                                                    <MenuItem value="3">Геодезическая разбивка</MenuItem>
                                                    <MenuItem value="4">Исполнительная съемка</MenuItem>
                                                    <MenuItem value="5">Выполнение</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <FormControl size="small" sx={{ minWidth: { xs: 80, sm: 'auto' } }}>
                                                <Select
                                                    value={viewMode}
                                                    onChange={handleViewModeChange}
                                                    sx={{
                                                        bgcolor: darkMode ? 'grey.800' : 'white',
                                                        color: darkMode ? 'white' : 'grey.800',
                                                        borderColor: darkMode ? 'grey.700' : 'grey.300',
                                                        minHeight: { xs: 28, sm: 'auto' },
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                        '& .MuiSelect-select': {
                                                            py: { xs: 0.5, sm: 1 },
                                                        }
                                                    }}
                                                    MenuProps={{
                                                        PaperProps: {
                                                            sx: {
                                                                '& .MuiMenuItem-root': {
                                                                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                                                    padding: { xs: '4px 8px', sm: '4px 8px' },
                                                                    minHeight: { xs: '28px', sm: '32px' }
                                                                },
                                                                '& .MuiListSubheader-root': {
                                                                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                                                    padding: { xs: '4px 8px', sm: '4px 8px' },
                                                                    lineHeight: 1.2,
                                                                    fontWeight: 'bold',
                                                                    color: 'black'
                                                                }
                                                            }
                                                        },
                                                    }}
                                                >
                                                    <MenuItem value="daily">По дням</MenuItem>
                                                    <MenuItem value="weekly">По неделям</MenuItem>
                                                    <MenuItem value="monthly">По месяцам</MenuItem>
                                                    <MenuItem value="yearly">По годам</MenuItem>
                                                    <MenuItem value="total">Всего</MenuItem>
                                                </Select>
                                            </FormControl>

                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                onClick={handleToggleChart}
                                                startIcon={showChart ? <ListIcon fontSize="small" /> : <ChartIcon fontSize="small" />}
                                                sx={{
                                                    minHeight: { xs: 28, sm: 32 },
                                                    px: { xs: 0.4, sm: 1 },
                                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                    py: { xs: 0.2, sm: 'auto' }
                                                }}
                                            >
                                                {showChart ? 'Данные' : 'График'}
                                            </Button>
                                        </Box>

                                        {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <IconButton
                                                onClick={handleToggleDark}
                                                sx={{
                                                    color: darkMode ? 'white' : 'inherit',
                                                    minWidth: { xs: 28, sm: 'auto' },
                                                    minHeight: { xs: 28, sm: 'auto' },
                                                    p: { xs: 0.5, sm: 0.5 }
                                                }}
                                            >
                                                {darkMode ? (
                                                    <LightModeIcon fontSize="small" />
                                                ) : (
                                                    <DarkModeIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        </Box> */}
                                    </Box>

                                    {/* <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        justifyContent: { xs: 'center', sm: 'flex-start' },
                                        py: { xs: 0, sm: 0 }
                                    }}>
                                        <Checkbox
                                            checked={isDisplayMounted}
                                            onChange={handleToggleDisplayMounted}
                                            color="primary"
                                            sx={{
                                                padding: 0,
                                                margin: 0,
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                                },
                                                color: darkMode ? 'white' : 'inherit',
                                                minWidth: { xs: 28, sm: 'auto' },
                                                minHeight: { xs: 28, sm: 'auto' }
                                            }}
                                        />
                                        <Typography variant="caption" sx={{
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                            textAlign: { xs: 'center', sm: 'left' }
                                        }}>
                                            Отобразить выполнение на карте
                                        </Typography>
                                    </Box> */}

                                    {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Checkbox
                                                checked={statisticForMounted}
                                                onChange={handleToggleStatisticForMounted}
                                                color="primary"
                                                sx={{
                                                    padding: 0,
                                                    margin: 0,
                                                    '& .MuiSvgIcon-root': {
                                                        fontSize: '1.25rem',
                                                    },
                                                    color: darkMode ? 'white' : 'inherit',
                                                }}
                                            />
                                            <Typography variant="caption" sx={{ fontSize: '0.875rem' }}>
                                                Статистика по выполненным
                                            </Typography>
                                        </Box> */}
                                </Stack>

                                {!showChart && (
                                    <Box>
                                        {keys.length > 0 && viewMode !== 'total' && (
                                            <Tabs
                                                value={tabValue}
                                                onChange={handleTabChange}
                                                variant="scrollable"
                                                scrollButtons="auto"
                                                sx={{
                                                    mb: { xs: 0.3, sm: 0.5 },
                                                    color: darkMode ? 'white' : 'inherit',
                                                    minHeight: { xs: 26, sm: 32 },
                                                    '& .MuiTab-root': {
                                                        minHeight: { xs: 26, sm: 32 },
                                                        py: { xs: 0.3, sm: 0.5 },
                                                        px: { xs: 0.5, sm: 1 },
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                        color: darkMode ? 'white' : 'inherit',
                                                        minWidth: { xs: 100, sm: 'auto' }
                                                    },
                                                    '& .MuiTabs-scrollButtons': {
                                                        width: { xs: 40, sm: 40 }
                                                    }
                                                }}
                                            >
                                                {keys.map((key, index) => (
                                                    <Tab key={key} label={key} value={index} />
                                                ))}
                                            </Tabs>
                                        )}

                                        {selectedKey && aggregated[selectedKey] && (
                                            <ContentRenderer
                                                stats={aggregated[selectedKey]}
                                                darkMode={darkMode}
                                                formatNumber={formatNumber}
                                            />
                                        )}
                                    </Box>
                                )}

                                {showChart && (
                                    <Box sx={{
                                        mt: { xs: 1.5, sm: 1 },
                                        p: { xs: 0.5, sm: 1 },
                                        borderRadius: 1,
                                        bgcolor: darkMode ? 'black' : 'white',
                                        height: { xs: '350px', sm: '500px' },
                                        width: '100%'
                                    }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart
                                                data={chartData}
                                                margin={{
                                                    top: 2,
                                                    right: window.innerWidth < 768 ? 5 : 2,
                                                    left: window.innerWidth < 768 ? 5 : 10,
                                                    bottom: window.innerWidth < 768 ? 5 : 2
                                                }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "#444" : "#ccc"} />
                                                <XAxis
                                                    dataKey="name"
                                                    tick={{
                                                        fill: darkMode ? "#fff" : "#333",
                                                        fontSize: window.innerWidth < 768 ? 8 : 10
                                                    }}
                                                    axisLine={{ stroke: darkMode ? "#555" : "#999" }}
                                                    tickLine={{ stroke: darkMode ? "#555" : "#999" }}
                                                    height={window.innerWidth < 768 ? 50 : 40}
                                                    interval={0}
                                                    angle={-25}
                                                    textAnchor="end"
                                                />
                                                <YAxis
                                                    tick={{
                                                        fill: darkMode ? "#fff" : "#333",
                                                        fontSize: window.innerWidth < 768 ? 8 : 10
                                                    }}
                                                    axisLine={{ stroke: darkMode ? "#555" : "#999" }}
                                                    tickLine={{ stroke: darkMode ? "#555" : "#999" }}
                                                />
                                                <RechartsTooltip
                                                    contentStyle={{
                                                        backgroundColor: darkMode ? 'rgba(30,30,30,0.9)' : 'rgba(255,255,255,0.9)',
                                                        color: darkMode ? 'white' : 'black',
                                                        borderRadius: '4px',
                                                        border: darkMode ? '1px solid #444' : '1px solid #ddd',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                        fontSize: window.innerWidth < 768 ? '11px' : '12px',
                                                        padding: window.innerWidth < 768 ? '8px' : '6px'
                                                    }}
                                                    labelFormatter={(label) => (
                                                        <Typography variant="caption" sx={{
                                                            fontWeight: 600,
                                                            fontSize: window.innerWidth < 768 ? '10px' : '11px'
                                                        }}>
                                                            {label}
                                                        </Typography>
                                                    )}
                                                />
                                                <Legend
                                                    onClick={handleLegendClick}
                                                    wrapperStyle={{
                                                        paddingTop: '5px',
                                                        fontSize: window.innerWidth < 768 ? '10px' : '11px',
                                                        lineHeight: window.innerWidth < 768 ? '12px' : '14px'
                                                    }}
                                                    iconSize={window.innerWidth < 768 ? 10 : 12}
                                                />
                                                <Line type="monotone" dataKey="wooden" stroke="#8884d8" name="Деревянные опоры" hide={hidden['wooden']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="concrete" stroke="#82ca9d" name="Железобетонные опоры" hide={hidden['concrete']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="c1" stroke="#ffc658" name="Опоры деревянные С1 9,5м" hide={hidden['c1']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="c2" stroke="#ff8042" name="Опоры деревянные С2 11м" hide={hidden['c2']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="sv95" stroke="#0088FE" name="Опоры ЖБ СВ9,5-3с" hide={hidden['sv95']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="sv110" stroke="#00C49F" name="Опоры ЖБ СВ110-5" hide={hidden['sv110']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="СИП-2" stroke="#FFBB28" name="СИП-2" hide={hidden['СИП-2']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="СИП-3" stroke="#FF8042" name="СИП-3" hide={hidden['СИП-3']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="СИП-4" stroke="#A28EFF" name="СИП-4" hide={hidden['СИП-4']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="runn" stroke="#FF4444" name="РУНН" hide={hidden['runn']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="ktp" stroke="#00BFFF" name="КТП" hide={hidden['ktp']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="wooden04_95" stroke="#FF6B6B" name="Заземление деревоопор 0,4кВ 9,5м" hide={hidden['wooden04_95']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="wooden04_110" stroke="#4ECDC4" name="Заземление деревоопор 0,4кВ 11м" hide={hidden['wooden04_110']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="concrete04_95" stroke="#45B7D1" name="Заземление ЖБ опор 0,4кВ 9,5м" hide={hidden['concrete04_95']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="concrete04_110" stroke="#96CEB4" name="Заземление ЖБ опор 0,4кВ 11м" hide={hidden['concrete04_110']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="wooden10_95" stroke="#FFEEAD" name="Заземление деревоопор 10кВ 9,5м" hide={hidden['wooden10_95']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="wooden10_110" stroke="#D4A5A5" name="Заземление деревоопор 10кВ 11м" hide={hidden['wooden10_110']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="concrete10_95" stroke="#9B59B6" name="Заземление ЖБ опор 10кВ 9,5м" hide={hidden['concrete10_95']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="concrete10_110" stroke="#3498DB" name="Заземление ЖБ опор 10кВ 11м" hide={hidden['concrete10_110']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="wooden04_10_95" stroke="#FFEEAD" name="Заземление деревоопор 0,4-10кВ 9,5м" hide={hidden['wooden04_10_95']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="wooden04_10_110" stroke="#D4A5A5" name="Заземление деревоопор 0,4-10кВ 11м" hide={hidden['wooden04_10_110']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="concrete04_10_95" stroke="#9B59B6" name="Заземление ЖБ опор 0,4-10кВ 9,5м" hide={hidden['concrete04_10_95']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                                <Line type="monotone" dataKey="concrete04_10_110" stroke="#3498DB" name="Заземление ЖБ опор 0,4-10кВ 11м" hide={hidden['concrete04_10_110']} strokeWidth={1.5} dot={{ r: window.innerWidth < 768 ? 2 : 3 }} />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>,
            document.body
        )
    );
});

export default StatisticControl;
